import React, { useState } from 'react';
import { Maybe, v2_useOrder } from '@crocodille/common';
import ErrorEl from '@components/elements/error/error';
import SpinnerIcon from '@components/icons/spinner-icon';
import { useTranslation } from 'react-i18next';
import Button from '../../elements/button/button';
import styles from './repeat-order.module.scss';
import { formatCurrency } from '@utils/format';
import BBPickupIcon from '../../icons/bb-pickup-icon';
import BBCourierIcon from '../../icons/bb-courier-icon';
import Link from 'next/link';
import * as routing from '@routing';

const OrderCard: React.FC<React.PropsWithChildren<RepeatOrderProps>> = ({
  onRepeatOrder,
  lastOrder,
  pending = false,
  error,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [showAllItems, setShowAllItems] = useState(false);

  const rows = lastOrder.items.reduce((acc: any[], item) => {
    acc.push({ type: 'item', name: item.name, quantity: item.quantity });
    item.adjustments.forEach((adjustment) => {
      acc.push({
        type: 'adjustment',
        name: adjustment.name,
        quantity: adjustment.quantity,
      });
    });
    return acc;
  }, []);

  const visibleRows = showAllItems ? rows : rows.slice(0, 5);

  const handleDisplayAll = () => {
    setShowAllItems((prev) => !prev);
  };

  return (
    <div className={styles.orderContainer}>
      <div className={styles.orderDescription}>
        <div className={styles.items}>
          <div className={styles.itemList}>
            <table>
              <tbody>
                {visibleRows.map((row, index) => (
                  <tr key={index}>
                    <td className={row.type === 'item' ? styles.item : ''}>
                      {row.name}
                    </td>
                    <td>{row.quantity}x</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 5 && (
            <Button
              type="button"
              variant="basic"
              round
              className={styles.displayAllBtn}
              onClick={handleDisplayAll}
            >
              {showAllItems ? t('hide_btn') : t('all_btn')}
            </Button>
          )}

          <div className={styles.price}>
            <span>{t('order.price')}</span>
            {formatCurrency(lastOrder.total, 'CZK', language)}
          </div>
        </div>

        <div className={styles.iconContainer}>
          <div className={styles.shippingIcon}>
            {lastOrder.shippingMethodType === 'delivery' ? (
              <>
                <BBCourierIcon className={styles.icon} />
                <div className={styles.iconText}>{t('shipping.courier')}</div>
              </>
            ) : (
              <>
                <BBPickupIcon className={styles.icon} />
                <div className={styles.iconText}>
                  {t('shipping.in_restaurant')}
                </div>
              </>
            )}
          </div>

          <Button
            className={styles.repeatButton}
            type="button"
            variant="basic"
            onClick={onRepeatOrder}
          >
            {t('order.repeat')}
            <Button.Icon>{pending ? <SpinnerIcon /> : null}</Button.Icon>
          </Button>
        </div>
      </div>

      {error ? (
        <ErrorEl className={styles.error}>
          {t(`order.repeat_error.${error}`)}
        </ErrorEl>
      ) : null}
    </div>
  );
};

export interface RepeatOrderProps {
  onRepeatOrder(): void;
  lastOrder: NonNullable<ReturnType<typeof v2_useOrder>['data']>;
  pending?: boolean;
  error?: Maybe<string>;
}

const RepeatOrder: React.FC<React.PropsWithChildren<RepeatOrderProps>> = ({
  onRepeatOrder,
  lastOrder,
  pending = false,
  error,
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.container}>
      <div className={styles.titleContainer}>
        <h2>{t('order.repeat_title')}</h2>
        <Link
          href={{
            pathname: routing.ACCOUNT_ORDER_HISTORY,
            query: { scrollToContent: 'true' },
          }}
        >
          <Button round variant="basic" className={styles.allBtn}>
            {t('all_btn')}
          </Button>
        </Link>
      </div>
      <div className={styles.subtitle}>{t('order.repeat_subtitle')}</div>
      <div className={styles.orders}>
        <OrderCard
          onRepeatOrder={onRepeatOrder}
          lastOrder={lastOrder}
          pending={pending}
          error={error}
        />
      </div>
    </div>
  );
};

export default RepeatOrder;
